import type { WixCodeApiFactoryArgs } from '@wix/thunderbolt-symbols'
import type { PaidPlansWixCodeSdkWixCodeApi } from '..'
import { namespace } from '..'
import { PricingPlansApi } from './pricingPlansApi'
import { validateGuid } from './validation'
import { HttpClientBasedHttpApi } from './httpClientBasedHttpApi'
import { PaidPlansV2Api } from './paidPlansV2Api'

const USE_V2_ORDERS_SPEC = 'specs.thunderbolt.paidPlansSdkUseV2Orders'

export function PaidPlansSdkFactory({
	wixCodeNamespacesRegistry,
	appEssentials,
	platformEnvData,
}: WixCodeApiFactoryArgs): {
	[namespace]: PaidPlansWixCodeSdkWixCodeApi
} {
	const v1api = new PricingPlansApi(new HttpClientBasedHttpApi(appEssentials.httpClient))
	const v2Api = new PaidPlansV2Api(appEssentials.httpClient)

	async function ensureMemberIsLoggedIn() {
		const siteMembers = wixCodeNamespacesRegistry.get('user')
		if (!siteMembers.currentUser.loggedIn) {
			await siteMembers.promptLogin()
		}
	}

	return {
		[namespace]: {
			async getCurrentMemberOrders(limit?: number, offset?: number) {
				await ensureMemberIsLoggedIn()
				if (platformEnvData.site.experiments[USE_V2_ORDERS_SPEC]) {
					return v2Api.getCurrentMemberOrders(limit, offset)
				} else {
					return v1api.getCurrentMemberOrders(limit, offset)
				}
			},

			async orderPlan(planId: string) {
				validateGuid(planId)
				await ensureMemberIsLoggedIn()

				if (platformEnvData.site.experiments[USE_V2_ORDERS_SPEC]) {
					return v2Api.createOrder(planId)
				} else {
					return v1api.createOrder(planId)
				}
			},

			async cancelOrder(orderId: string) {
				validateGuid(orderId)
				await ensureMemberIsLoggedIn()

				if (platformEnvData.site.experiments[USE_V2_ORDERS_SPEC]) {
					return v2Api.cancelOrder(orderId)
				} else {
					return v1api.cancelOrder(orderId)
				}
			},

			async purchasePlan(planId: string) {
				validateGuid(planId)
				await ensureMemberIsLoggedIn()

				const wixPay = wixCodeNamespacesRegistry.get('pay')

				const { orderId, wixPayOrderId } = platformEnvData.site.experiments[USE_V2_ORDERS_SPEC]
					? await v2Api.createOrder(planId)
					: await v1api.createOrder(planId)

				if (!wixPayOrderId) {
					return { orderId }
				}

				const { status: wixPayStatus } = await wixPay.startPayment(wixPayOrderId, { showThankYouPage: true })

				return {
					orderId,
					wixPayOrderId,
					wixPayStatus,
				}
			},
		},
	}
}
